import { useState, useEffect } from 'react';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const companyFields = [
  { section: 'Date Identificare', icon: Building2, fields: [
    { label: 'Denumire companie *', field: 'company_name', placeholder: 'SC Exemplu SRL' },
    { label: 'CUI / CIF', field: 'cui', placeholder: 'RO12345678', mono: true },
    { label: 'Nr. Registrul Comertului', field: 'registration_number', placeholder: 'J12/1234/2020', mono: true },
    { label: 'Tara', field: 'country', placeholder: 'Romania' },
  ]},
  { section: 'Adresa si Contact', fields: [
    { label: 'Adresa', field: 'address', placeholder: 'Str. Exemplu, Nr. 1', span: 2 },
    { label: 'Oras', field: 'city', placeholder: 'Bucuresti' },
    { label: 'Judet', field: 'county', placeholder: 'Bucuresti' },
    { label: 'Email', field: 'email', placeholder: 'contact@exemplu.ro' },
    { label: 'Telefon', field: 'phone', placeholder: '+40 21 123 4567', mono: true },
  ]},
  { section: 'Date Bancare', fields: [
    { label: 'Banca', field: 'bank_name', placeholder: 'Banca Transilvania' },
    { label: 'IBAN', field: 'iban', placeholder: 'RO49 AAAA 1B31 0075 9384 0000', mono: true },
  ]},
  { section: 'Reprezentant Legal', fields: [
    { label: 'Nume reprezentant', field: 'legal_representative', placeholder: 'Ion Popescu' },
    { label: 'Functie', field: 'legal_representative_role', placeholder: 'Administrator' },
  ]},
];

const emptyProfile = {
  company_name: '', cui: '', registration_number: '', address: '', city: '', county: '',
  country: 'Romania', email: '', phone: '', bank_name: '', iban: '',
  legal_representative: '', legal_representative_role: '',
  institution_type: '', main_activity: '', postal_code: '', website: '',
  department: '', contact_email: '', seap_id: '', nuts_code: '', buyer_profile_url: ''
};

export default function CompanyProfilePage() {
  const { user } = useAuth();
  const [form, setForm] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fieldSections = companyFields;
  const pageTitle = 'Profil Companie';
  const pageDesc = 'Datele SAGA Infrastructura SRL pentru completarea automata a formularelor si ofertelor';

  useEffect(() => {
    API.get('/company-profile')
      .then(r => { if (r.data && Object.keys(r.data).length > 0) setForm({ ...emptyProfile, ...r.data }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error('Numele companiei este obligatoriu'); return; }
    setSaving(true);
    try {
      await API.post('/company-profile', form);
      toast.success('Profil salvat cu succes!');
    } catch { toast.error('Eroare la salvare'); }
    finally { setSaving(false); }
  };

  if (!user?.is_admin) return (
    <div className="p-8 flex flex-col items-center justify-center h-[60vh] text-center" data-testid="company-forbidden">
      <Building2 className="w-10 h-10 text-rose-400 mb-3" strokeWidth={1.5} />
      <p className="text-sm text-muted-foreground">Doar administratorul poate accesa Profilul Companiei.</p>
    </div>
  );
  if (loading) return <div className="p-8"><div className="h-96 bg-white/5 animate-pulse rounded-lg" /></div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="company-profile-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <Building2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
          {pageTitle}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{pageDesc}</p>
      </div>

      <div className="space-y-6">
        {fieldSections.map((section, si) => (
          <Card key={si} className="bg-card border-white/10">
            <CardHeader className="pb-4">
              <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>{section.section}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map((f, fi) => (
                <div key={fi} className={f.span === 2 ? 'md:col-span-2' : ''}>
                  <Label className="text-xs text-muted-foreground">{f.label}</Label>
                  <Input
                    value={form[f.field] || ''}
                    onChange={e => setForm({ ...form, [f.field]: e.target.value })}
                    placeholder={f.placeholder}
                    className={`mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm ${f.mono ? 'font-mono-data' : ''}`}
                    data-testid={`profile-${f.field}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} size="lg" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Se salveaza...' : 'Salveaza Profilul'}
          </Button>
        </div>
      </div>
    </div>
  );
}
